import React from 'react';
import { Box, Typography, useTheme, MenuItem } from '@mui/material';
import { tokens } from '../../theme';
import ListView from '../../SystemDesign/ListViewV2';
import SearchInput from '../../SystemDesign/SearchInput';
import { useGetAttemptsQuery, useGetAllQuizResponsesQuery } from '../../services/SkillsManagement';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../Reducers/Actions';
import { useAuth } from '../../Context/AuthContext';
import DateInTable from '../../SystemDesign/DateInTable';
import { formattedDate } from '../../Utils/Functions';
import RoleChip from '../../SystemDesign/RoleChip';
import ReviewAllAttempts from './ReviewAllAttempts';
import ReviewAttemptsPDFExport from './ReviewAttemptsPDFExport';
import DownloadIcon from '@mui/icons-material/Download';
import AddQuizEvaluation from './AddQuizEvaluation';
import ListViewMenuActions from '../../SystemDesign/ListViewMenuActions';

const QuizEvaluationList = ({ externalSearchTerm, onSearchChange, hideHeader = false, headerRight = null }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [columnFilters, setColumnFilters] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [reportState, setReportState] = React.useState({ open: false, quizId: null });
  const [takeQuizState, setTakeQuizState] = React.useState({ open: false, quizId: null, posteId: null });
  const [pdfState, setPdfState] = React.useState({ open: false, quizId: null });

  const [queryParams, setQueryParams] = React.useState(() => {
    const savedRowsPerPage = localStorage.getItem('quiz-responses-rowsPerPage');
    const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
    return {
      // Always include current user filter
      filters: JSON.stringify({ userId: [String(user?.id || user?.userId || user?._id || '')] }),
      sorting: '',
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage }),
    };
  });

  // Fetch attempts for current user to compute last tentative/score/time per quiz
  const attemptsArgs = React.useMemo(() => ({
    userId: Number(user?.id || user?.userId || user?._id || 0) || undefined,
    page: 1,
    limit: 200, // fetch enough to group client-side
  }), [user]);
  const { data: attemptsData, isLoading: attemptsLoading, isFetching: attemptsFetching, refetch: refetchAttempts } = useGetAttemptsQuery(attemptsArgs, {
    refetchOnMountOrArgChange: true,
    skip: !attemptsArgs.userId,
  });

  // Keep attempts slice in sync with server
  React.useEffect(() => {
    const list = attemptsData?.data?.quizAttempts || attemptsData?.quizAttempts;
    if (Array.isArray(list)) {
      dispatch({ type: actions.INITIALIZE_QUIZ_ATTEMPTS, payload: list });
    }
  }, [attemptsData, dispatch]);

  const attemptsFromStore = useSelector((s) => s.quizAttempts);

  // When report dialog opens, fetch responses for that quiz and current user and store them
  const responsesArgs = React.useMemo(() => ({
    quizId: reportState.quizId || undefined,
    userId: Number(user?.id || user?.userId || user?._id || 0) || undefined,
    page: 1,
    limit: 100,
  }), [reportState.quizId, user]);
  const { data: responsesData } = useGetAllQuizResponsesQuery(responsesArgs, {
    skip: !reportState.open || !responsesArgs.quizId || !responsesArgs.userId,
    refetchOnMountOrArgChange: true,
  });
  React.useEffect(() => {
    if (!reportState.open) return;
    const list = responsesData?.data?.quizResponses || responsesData?.quizResponses;
    if (Array.isArray(list)) {
      dispatch({ type: actions.INITIALIZE_QUIZ_RESPONSES, payload: list });
    }
  }, [reportState.open, responsesData, dispatch]);

  // Group attempts by quiz and compute last attempt summary
  const grouped = React.useMemo(() => {
    const list = (Array.isArray(attemptsFromStore) && attemptsFromStore.length)
      ? attemptsFromStore
      : (attemptsData?.data?.quizAttempts || attemptsData?.quizAttempts || []);
    const byQuiz = new Map();
    for (const a of list) {
      const key = a.quizId;
      const entry = byQuiz.get(key) || { quizId: key, title: a?.quiz?.title || `Quiz #${key}`, posteId: a?.quiz?.posteId || a?.posteId || null, attempts: [] };
      entry.attempts.push(a);
      entry.title = a?.quiz?.title || entry.title;
      entry.posteId = a?.quiz?.posteId || a?.posteId || entry.posteId;
      byQuiz.set(key, entry);
    }
    // Build rows: one per quiz
    const rows = Array.from(byQuiz.values()).map((e) => {
      // sort attempts by createdAt/attemptId to find latest
      const sorted = [...e.attempts].sort((x, y) => {
        const dx = new Date(x.updatedAt || x.createdAt || x.startTime || 0).getTime();
        const dy = new Date(y.updatedAt || y.createdAt || y.startTime || 0).getTime();
        return dy - dx;
      });
      const last = sorted[0] || {};
      return {
        quizId: e.quizId,
        title: e.title,
        posteId: e.posteId || null,
        tentative: e.attempts.length,
        lastAttemptId: last.attemptId,
        score: last.score ?? null,
        timeSpent: last.timeSpent ?? null,
        status: last.status || '-',
        createdAt: last.endTime || last.updatedAt || last.createdAt || last.startTime || null,
      };
    });
    return rows;
  }, [attemptsData, attemptsFromStore]);

  // Search by title
  const filtered = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return grouped;
    return grouped.filter((r) => (r.title || '').toLowerCase().includes(term));
  }, [grouped, searchTerm]);

  // Local pagination for grouped rows
  const localPagination = React.useMemo(() => {
    const p = JSON.parse(queryParams.pagination || '{}');
    const currentPage = p.currentPage || 1;
    const rowsPerPage = p.rowsPerPage || 10;
    const total = filtered.length;
    const start = (currentPage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return { currentPage, rowsPerPage, total, totalPages: Math.ceil(total / rowsPerPage), slice: [start, end] };
  }, [queryParams.pagination, filtered]);

  const rows = React.useMemo(() => filtered.slice(localPagination.slice[0], localPagination.slice[1]), [filtered, localPagination]);

  // Format seconds to minutes/seconds label
  const fmtSeconds = React.useCallback((secs) => {
    if (secs == null) return '-';
    const s = Number(secs) || 0;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m > 0) return `${m} min${rs ? ` ${rs} s` : ''}`;
    return `${rs} s`;
  }, []);

  const columns = React.useMemo(
    () => [
      { id: 'title', accessorKey: 'title', header: 'Quiz Title', enableColumnFilter: false, enableSorting: false },
      { id: 'tentative', accessorKey: 'tentative', header: 'Tentative', enableColumnFilter: false, enableSorting: false },
      {
        id: 'score',
        accessorKey: 'score',
        header: 'Score',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'timeSpent',
        accessorKey: 'timeSpent',
        header: 'Time',
        cell: (info) => fmtSeconds(info.getValue()),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          const s = String(info.getValue() || '').toLowerCase();
          let label = s;
          let colorOverride = colors.green[700];
          let bgColorOverride = colors.green[100];
          if (s === 'completed') {
            label = 'completed';
            colorOverride = colors.green[700];
            bgColorOverride = colors.green[100];
          } else if (s === 'in_progress' || s === 'in progress') {
            label = 'in progress';
            colorOverride = colors.Orange[400];
            bgColorOverride = colors.Orange[100];
          } else if (s === 'expired') {
            label = 'expired';
            colorOverride = colors.redAccent[600];
            bgColorOverride = colors.redAccent[100];
          }
          return <RoleChip role={label} colorOverride={colorOverride} bgColorOverride={bgColorOverride} />;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Last attempt at',
        cell: (info) => info.getValue() ? <DateInTable date={formattedDate(new Date(info.getValue()))} /> : '-',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'actions',
        header: '',
        cell: (info) => {
          const row = info?.row?.original || {};
          const qid = row?.quizId || row?.quiz?.quizId || row?.quiz?.id;
          const pid = row?.posteId || row?.quiz?.posteId || row?.poste?.id;
          const status = String(row?.status || '').toLowerCase();
          const canComplete = status === 'in_progress' || status === 'in progress';
          return (
            <ListViewMenuActions>
              <MenuItem onClick={() => setReportState({ open: true, quizId: qid })}>View report</MenuItem>
              <MenuItem onClick={() => setPdfState({ open: true, quizId: qid })} disabled={!qid}>
                <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
                Download PDF
              </MenuItem>
              <MenuItem
                disabled={!canComplete || !qid}
                onClick={canComplete ? () => setTakeQuizState({ open: true, quizId: qid, posteId: pid }) : undefined}
              >
                Complete quiz
              </MenuItem>
            </ListViewMenuActions>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ], []
  );

  // When the take-quiz dialog closes, refetch attempts to update status/score
  React.useEffect(() => {
    if (!takeQuizState.open) {
      const timers = [];
      const schedule = (ms) => timers.push(setTimeout(() => { try { refetchAttempts?.(); } catch (_) {} }, ms));
      schedule(200);   // immediate
      schedule(1000);  // after 1s
      schedule(2000);  // after 2s
      return () => { timers.forEach(clearTimeout); };
    }
  }, [takeQuizState.open, refetchAttempts]);

  const applyFilters = (updatedFilters = columnFilters, paginationInfo = null, sortingInfo = null, searchValue = null) => {
    const filtersArray = updatedFilters || [];
    const formatted = filtersArray.reduce((acc, filter) => {
      if (typeof filter === 'object' && !Array.isArray(filter)) {
        const [key, values] = Object.entries(filter)[0];
        const arr = Array.isArray(values) ? values.filter((v) => v !== '') : [String(values)];
        if (arr.length) acc[key] = arr.map(String);
      }
      return acc;
    }, {});

    // Enforce current user filter always
    const uid = String(user?.id || user?.userId || user?._id || '');
    if (uid) formatted.userId = [uid];

    // Persist rowsPerPage
    let rowsPerPage = paginationInfo?.rowsPerPage;
    if (!rowsPerPage) {
      const saved = localStorage.getItem('quiz-responses-rowsPerPage');
      rowsPerPage = saved !== null ? parseInt(saved, 10) : 10;
    } else {
      localStorage.setItem('quiz-responses-rowsPerPage', String(rowsPerPage));
    }

    setColumnFilters(filtersArray);
    setQueryParams((prev) => ({
      ...prev,
      filters: JSON.stringify(formatted),
      pagination: JSON.stringify({ currentPage: paginationInfo?.currentPage || 1, rowsPerPage }),
      sorting: sortingInfo ? JSON.stringify(sortingInfo) : prev.sorting,
    }));
  };

  const handleSearchChange = (value) => {
    if (onSearchChange) onSearchChange(value);
    setSearchTerm(value);
    if (!value) applyFilters(null, null, null, '');
  };
  const handleSearchSubmit = (value) => {
    if (onSearchChange) onSearchChange(value);
    setSearchTerm(value);
  };

  // If external term is provided, mirror it locally
  React.useEffect(() => {
    if (typeof externalSearchTerm === 'string' && externalSearchTerm !== searchTerm) {
      setSearchTerm(externalSearchTerm);
    }
  }, [externalSearchTerm]);

  return (
    // Remove extra padding so header aligns closely with parent profile header (matches Oral view spacing)
    <Box sx={{ p: 0 }}>
      {!hideHeader && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <SearchInput
              placeholder="Search by quiz title..."
              value={externalSearchTerm ?? searchTerm}
              onChange={(v) => {
                if (onSearchChange) onSearchChange(v);
                setSearchTerm(v);
              }}
            />
          </Box>
          {/* Optional right header content (e.g., ViewToggle) */}
          {headerRight ? (
            <Box sx={{ ml: 'auto' }}>
              {headerRight}
            </Box>
          ) : null}
        </Box>
      )}
      <ListView
        columns={columns}
        dispatchData={rows}
        tableName="quiz-evaluations"
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        applyFilters={() => {}}
        pagination={{ total: localPagination.total, totalPages: localPagination.totalPages, currentPage: localPagination.currentPage, rowsPerPage: localPagination.rowsPerPage }}
        setQueryParams={setQueryParams}
        queryParams={queryParams}
        existingFilters={false}
        existingPagination={true}
        debouncedSearchValue={externalSearchTerm ?? searchTerm}
        isFilterOpen={false}
        setIsFilterOpen={() => {}}
      />
      <ReviewAllAttempts
        open={reportState.open}
        onClose={() => setReportState({ open: false, quizId: null })}
        quizId={reportState.quizId}
        userId={user?.id}
      />
      <AddQuizEvaluation
        open={takeQuizState.open}
        onClose={() => setTakeQuizState({ open: false, quizId: null, posteId: null })}
        quizId={takeQuizState.quizId}
        posteId={takeQuizState.posteId}
        onAttemptUpdated={() => { try { refetchAttempts?.(); } catch (_) {} }}
      />
      <ReviewAttemptsPDFExport
        open={pdfState.open}
        onClose={() => setPdfState({ open: false, quizId: null })}
        quizId={pdfState.quizId}
        userId={user?.id || user?.userId || user?._id}
      />
    </Box>
  );
};

export default QuizEvaluationList;
